import React, { useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { useCoverage } from '../Coverage/CoverageContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { PeriodNavigationActions } from './PeriodNavigationActions';

interface CoverageActionsProps {
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
  variant?: 'external' | 'inline';
}

export const CoverageActions = ({
  setDisableDragNnDropTooltip,
  isDragging,
  variant = 'external',
}: CoverageActionsProps) => {
  const {
    coverageDate,
    navigateToNextPeriod,
    navigateToPreviousPeriod,
    getNextPeriodDateRange,
    getPreviousPeriodDateRange,
    selectedSourceIdentifiers,
    isLoadingPeriodNavigation,
    wouldNextPeriodBeInFuture,
  } = useCoverage();

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const onNext = useCallback(() => {
    if (!coverageDate || wouldNextPeriodBeInFuture()) return;
    navigateToNextPeriod();
  }, [coverageDate, wouldNextPeriodBeInFuture, navigateToNextPeriod]);

  const onPrevious = useCallback(() => {
    if (!coverageDate) return;
    navigateToPreviousPeriod();
  }, [coverageDate, navigateToPreviousPeriod]);

  const isPreviousDisabled = !coverageDate;
  const isNextDisabled = !coverageDate || wouldNextPeriodBeInFuture();

  const nextPeriodDateRange = useMemo(
    () => getNextPeriodDateRange(),
    [getNextPeriodDateRange],
  );

  const previousPeriodDateRange = useMemo(
    () => getPreviousPeriodDateRange(),
    [getPreviousPeriodDateRange],
  );

  const tooltips = useMemo(() => {
    const formatPeriodRange = (dateRange: DateRange | undefined): string => {
      if (!dateRange?.from || !dateRange?.to) return '';

      const fromStr = formatDateTimeWithTimeZone(dateRange.from);
      const toStr = formatDateTimeWithTimeZone(dateRange.to);

      return `${fromStr} to ${toStr}`;
    };

    return {
      previousTooltip: previousPeriodDateRange
        ? `Switch to coverage for period ${formatPeriodRange(
            previousPeriodDateRange,
          )}`
        : 'Switch to previous coverage period',
      nextTooltip: nextPeriodDateRange
        ? `Switch to coverage for period ${formatPeriodRange(
            nextPeriodDateRange,
          )}\nLatest: now (cannot navigate into future)`
        : 'Switch to next coverage period',
    };
  }, [
    previousPeriodDateRange,
    nextPeriodDateRange,
    formatDateTimeWithTimeZone,
  ]);

  return (
    <PeriodNavigationActions
      onNext={onNext}
      onPrevious={onPrevious}
      isLoading={isLoadingPeriodNavigation}
      isPreviousDisabled={isPreviousDisabled}
      isNextDisabled={isNextDisabled}
      previousTooltip={tooltips.previousTooltip}
      nextTooltip={tooltips.nextTooltip}
      shouldShow={selectedSourceIdentifiers.length > 0}
      setDisableDragNnDropTooltip={setDisableDragNnDropTooltip}
      isDragging={isDragging}
      variant={variant}
    />
  );
};
